import * as React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { motion } from 'motion/react';
import { LayoutDashboard, User, Briefcase, Wallet, Users, LogOut, Settings } from 'lucide-react';
import { hasRole, useXyqarAuth } from '@xyqar/shared-auth';

const logo = '/logo.png';

export default function PortalShell() {
  const { principal, loading, login, logout } = useXyqarAuth();

  React.useEffect(() => { if (!loading && !principal) login(); }, [loading, principal, login]);
  if (loading || !principal) return <Loading />;

  const ok = hasRole(principal, 'expert', 'admin', 'super_admin');
  if (!ok) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-center p-8">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-red-400 font-mono">403</p>
          <h1 className="text-3xl text-white mt-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Forbidden</h1>
          <p className="text-sm text-white/60 mt-3">Expert role required.</p>
          <button onClick={() => logout()} className="mt-6 text-xs text-white/60 hover:text-white">Logout</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex">
      <a href="#expert-main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded-md focus:text-xs focus:font-mono focus:uppercase focus:tracking-[0.2em]">Skip to content</a>
      <aside className="w-64 border-r border-white/10 bg-black/80 backdrop-blur-[20px] flex flex-col p-6 gap-2" aria-label="Primary navigation">
        <div className="flex items-center gap-3 mb-8">
          <img src={logo} alt="XYQAR" className="h-7 w-auto" />
          <div>
            <p className="text-[18px] font-bold tracking-[0.2em] text-white leading-none">XYQAR</p>
            <p className="text-[9px] tracking-[0.15em] text-white/40 uppercase mt-1">Expert Room</p>
          </div>
        </div>

        <NavItem to="/dashboard" icon={<LayoutDashboard className="h-4 w-4" />} label="Dashboard" />
        <NavItem to="/profile"   icon={<User           className="h-4 w-4" />} label="Profile" />
        <NavItem to="/missions"  icon={<Briefcase      className="h-4 w-4" />} label="Missions" />
        <NavItem to="/payments"  icon={<Wallet         className="h-4 w-4" />} label="Payments" />
        <NavItem to="/referral"  icon={<Users          className="h-4 w-4" />} label="Referral" />
        <NavItem to="/account"   icon={<Settings       className="h-4 w-4" />} label="Account" />

        <div className="mt-auto flex flex-col gap-3">
          <a href="https://xyqar.com/trust" target="_blank" rel="noreferrer" className="border border-white/10 rounded-md p-3 bg-gradient-to-br from-white/[0.04] to-white/[0.02] hover:border-white/20 transition">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-mono">Trust →</p>
            <p className="text-[11px] text-white/80 leading-snug mt-1">Audit chain · 96 k events · Merkle anchored</p>
          </a>
          <div className="border border-white/10 rounded-md p-3 bg-white/5">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-mono">EXPERT</p>
            <p className="text-sm text-white truncate">{principal.email}</p>
          </div>
          <button onClick={() => logout()} className="flex items-center gap-2 text-xs text-white/60 hover:text-white px-3 py-2">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <main id="expert-main" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="p-10 max-w-5xl">
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}

function NavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink to={to} className={({ isActive }) =>
      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ' +
      (isActive ? 'bg-white/10 text-white border border-white/15' : 'text-white/60 hover:text-white hover:bg-white/5 border border-transparent')}>
      {icon}{label}
    </NavLink>
  );
}

function Loading() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <motion.div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full"
        animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }} />
    </div>
  );
}
